import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import {
  ActionBar,
  DataGrid,
  Navigator,
  readStored,
  SplitView,
  writeStored,
  type DesktopAction,
  type GridColumn,
  type NavNode,
} from '@/components/desktop';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  Modal,
  OkNote,
  Pager,
  SearchBar,
  Select,
  Tile,
  errorMessage,
  quantity,
  shortDate,
} from '@/components/ui';
import { AppIcon, categoryKind } from '@/components/AppIcon';
import { FarmPicker, NEW_FARM } from '@/components/FarmPicker';
import { ReviewBanner, ReviewPill } from '@/components/Review';
import { sameFarmName } from '@/components/farmMatch';
import { PRECISION_OPTIONS } from '@/format';
import { SkuForm } from './Inventory';
import { bodyText, createOp, needsTheLine, resendReference, saveReference, usePendingInPanel, type ReferenceSaved } from './referenceOffline';
import { isPendingRow, pendingCategory, pendingSku, pendingSupplier, pendingUom } from '@/offline/pending';
import { findItems, useCodePreview } from '@/hooks/inventory';
import { isOfflineGap, NOT_ON_THIS_PC } from '@/offline/availability';
import { useConnection } from '@/stores/connection';
import type { Operation } from '@/offline/outbox';
import { savedWork } from '@/offline/engine';
import { useScreenIntent, useShellNav } from '@/stores/shellNav';
import { BinsPanel, StoreBinsTab } from './StorageBins';
import { DeletedNote, PendingRecordPage, RecordPage, type Notice } from './referencePage';
import { useOpenRecord } from '@/hooks/useOpenRecord';

type SkuRow = components['schemas']['SkuListItemDto'];
type StoreRow = components['schemas']['StoreDto'];
type SupplierRow = components['schemas']['SupplierDto'];
type CategoryRow = components['schemas']['SkuCategoryDto'];
type UomRow = components['schemas']['UomDto'];
type RetireResult = components['schemas']['RetireResultDto'];
type FarmRow = components['schemas']['FarmDto'];
type FarmCreated = components['schemas']['FarmCreatedDto'];

import { useAuth } from '@/stores/auth';
import { holdsSentence, packageLabel, packageSentence, packagingsOf, packedBreakdown } from '@/packing';

const PAGE_SIZE = 50;

type RefCategory = 'items' | 'stores' | 'bins' | 'suppliers' | 'categories' | 'units';

const ACTIVE_SECTION_KEY = 'poultry.reference.category';

/** Which list puts right each kind of entry added on this PC. */
const FIX_SECTION: Partial<Record<string, RefCategory>> = {
  'sku.create': 'items',
  'category.create': 'categories',
  'uom.create': 'units',
  'supplier.create': 'suppliers',
};

/**
 * Items, stores, storage bins, suppliers, categories and units in one place — the things
 * a document points at, rather than a document itself. Folded here from
 * separate tabs because nobody looks at them side by side; they pick one
 * and work in it for a while.
 */
export function Reference() {
  const { can } = useAuth();
  const [category, setCategory] = useState<RefCategory>(() => readStored(ACTIVE_SECTION_KEY, 'items'));

  // Sent here from Needs attention to put right something added on this PC
  // that the server refused: its list, with its form open.
  const intent = useScreenIntent('inv-reference');
  const [fixOp, setFixOp] = useState<Operation | null>(null);
  useEffect(() => {
    const opId = intent?.fixOperation;
    if (!opId) return;
    void savedWork().then((ops) => {
      const op = ops.find((o) => o.id === opId);
      const section = op && FIX_SECTION[op.kind];
      if (!op || !section) return;
      setCategory(section);
      setFixOp(op);
    });
  }, [intent?.fixOperation]);
  const fixFor = (section: RefCategory) => (fixOp && FIX_SECTION[fixOp.kind] === section ? fixOp : undefined);

  // The same queries the item form uses, so a category or unit added there
  // shows up in the sidebar count without another request.
  const categories = useQuery({
    queryKey: ['inv', 'categories', false],
    queryFn: () => api.get('/api/v1/inventory/categories'),
    enabled: can('inv.sku.read'),
  });
  const uoms = useQuery({
    queryKey: ['inv', 'uoms', false],
    queryFn: () => api.get('/api/v1/inventory/uoms'),
    enabled: can('inv.sku.read'),
  });
  const toReview: Partial<Record<RefCategory, number>> = {
    categories: countToReview(categories.data),
    units: countToReview(uoms.data),
  };

  const navNodes: NavNode[] = (
    [
      { id: 'items' as const, icon: <AppIcon kind="item" />, label: 'SKU', hint: 'Feed, medicine and other stock items', permission: 'inv.sku.read' },
      { id: 'categories' as const, icon: <AppIcon kind="category" />, label: 'Categories', hint: 'How items are grouped', permission: 'inv.sku.read' },
      { id: 'units' as const, icon: <AppIcon kind="unit" />, label: 'Units', hint: 'How items are counted', permission: 'inv.sku.read' },
      { id: 'stores' as const, icon: <AppIcon kind="store" />, label: 'Stores', hint: 'Where stock is kept', permission: 'inv.store.read' },
      { id: 'bins' as const, icon: <AppIcon kind="bin" />, label: 'Storage Bins', hint: 'Optional spots inside a store', permission: 'inv.store.read' },
      { id: 'suppliers' as const, icon: <AppIcon kind="supplier" />, label: 'Suppliers', hint: 'Who stock is bought from', permission: 'inv.supplier.read' },
    ] as const
  )
    .filter((n) => can(n.permission))
    // A badge only while something waits for review, so the sidebar stays quiet otherwise.
    .map(({ permission: _permission, ...n }) => ({ ...n, count: toReview[n.id] || undefined }));

  const active = navNodes.some((n) => n.id === category) ? category : ((navNodes[0]?.id as RefCategory) ?? 'items');

  return (
    <div className="workbench">
      <SplitView
        id="reference"
        initial={200}
        firstLabel="reference list"
        first={
          <Navigator
            id="reference"
            label="Items Manager"
            nodes={navNodes}
            selectedId={active}
            onSelect={(id) => {
              setCategory(id as RefCategory);
              writeStored(ACTIVE_SECTION_KEY, id);
            }}
          />
        }
        second={
          active === 'items' ? (
            <ItemsPanel key={fixFor('items')?.id ?? 'list'} fixOperation={fixFor('items')} />
          ) : active === 'categories' ? (
            <CategoriesPanel key={fixFor('categories')?.id ?? 'list'} fixOperation={fixFor('categories')} />
          ) : active === 'units' ? (
            <UnitsPanel key={fixFor('units')?.id ?? 'list'} fixOperation={fixFor('units')} />
          ) : active === 'stores' ? (
            <StoresPanel />
          ) : active === 'bins' ? (
            <BinsPanel />
          ) : active === 'suppliers' ? (
            <SuppliersPanel key={fixFor('suppliers')?.id ?? 'list'} fixOperation={fixFor('suppliers')} />
          ) : (
            <div className="panel panel--empty">
              <p className="muted">You do not have access to any reference lists.</p>
            </div>
          )
        }
      />
    </div>
  );
}

// ------------------------------------------------------------------ Items

/** An item's own screen: how it is counted and packed, when it runs short, and how it is tracked. */
function ItemPage({ row }: { row: SkuRow }) {
  return (
    <>
      {!row.isActive && <DeletedNote what="item" />}
      <div className="tiles">
        <Tile label="Unit" value={row.uom} note={holdsSentence(row) ?? undefined} />
        <Tile
          label="Package"
          value={defaultPackage(row)}
          note={packagingsOf(row).length > 1 ? `and ${packagingsOf(row).length - 1} other size(s)` : undefined}
        />
        <Tile
          label="Reorder at"
          value={row.reorderLevel ? quantity(row.reorderLevel, row.uomPrecision) : '—'}
          note={(row.reorderLevel && packedBreakdown(row.reorderLevel, row)) || undefined}
        />
        <Tile label="Tracking" value={row.isBatchTracked ? (row.hasExpiry ? 'By lot, with expiry' : 'By lot') : row.hasExpiry ? 'Expiry only' : 'Not tracked'} />
      </div>
      <section className="stack docpage__section">
        <h3 className="formsection">Item</h3>
        <ItemDetails row={row} />
      </section>
    </>
  );
}

/** The package quantities are shown in, in a few words: "Carton of 50 × 1 KG". */
function defaultPackage(row: SkuRow): string {
  const p = packagingsOf(row).find((x) => x.isDefault) ?? packagingsOf(row)[0];
  return p ? packageLabel(p, row.uom) : '—';
}

function ItemDetails({ row }: { row: SkuRow }) {
  return (
    <dl className="detaillist">
      <dt>Code</dt>
      <dd className="mono">{row.code}</dd>
      <dt>Name</dt>
      <dd>
        {row.nameEn}
        {row.nameUr && <span className="urdu"> · {row.nameUr}</span>}
      </dd>
      <dt>Category</dt>
      <dd>{row.category}</dd>
      <dt>Unit</dt>
      <dd>
        {row.uom}
        {holdsSentence(row) && <span className="dim"> · {holdsSentence(row)}</span>}
      </dd>
      <dt>Packages</dt>
      <dd>
        {packagingsOf(row).length === 0
          ? '—'
          : packagingsOf(row).map((p) => (
              <div key={p.id}>
                {packageSentence(p, row)}
                {p.isDefault && packagingsOf(row).length > 1 && <span className="dim"> · default</span>}
              </div>
            ))}
      </dd>
      <dt>Reorder level</dt>
      <dd>{row.reorderLevel ? quantity(row.reorderLevel, row.uomPrecision) : '—'}</dd>
      <dt>Tracking</dt>
      <dd>
        {row.isBatchTracked ? 'By lot' : 'Not tracked'}
        {row.hasExpiry && ', with expiry'}
      </dd>
      <dt>Status</dt>
      <dd>{row.isActive ? <span className="pill">Active</span> : <span className="pill pill--alert">⚠ Deleted</span>}</dd>
    </dl>
  );
}

function ItemsPanel({ fixOperation }: { fixOperation?: Operation | undefined }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [form, setForm] = useState<'create' | 'edit' | 'fix' | null>(fixOperation ? 'fix' : null);
  const [fixOp, setFixOp] = useState<Operation | null>(fixOperation ?? null);
  const online = useConnection((s) => s.online);
  const [notice, setNotice] = useState<Notice>(null);

  const { data: categories } = useQuery({
    queryKey: ['inv', 'categories', false],
    queryFn: () => api.get('/api/v1/inventory/categories'),
  });

  const query = {
    search: search || undefined,
    categoryId: categoryId || undefined,
    includeInactive: showRetired ? 'true' : undefined,
    take: PAGE_SIZE,
    skip,
  };
  const list = useQuery({
    queryKey: ['inv', 'skus', 'reference', query],
    queryFn: () => api.get('/api/v1/inventory/skus', query),
  });
  const { data, isPending, isError, error } = list;

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['inv', 'skus'] });

  const retire = useMutation({
    mutationFn: (id: string) => api.delete<RetireResult>(`/api/v1/inventory/skus/${id}`),
    onSuccess: (result) => {
      invalidate();
      afterRetire(result);
      setNotice({ tone: 'ok', message: result.message });
    },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not delete the item') }),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => api.patch(`/api/v1/inventory/skus/${id}`, { isActive: true }),
    onSuccess: () => {
      invalidate();
      page.patch({ isActive: true });
      setNotice({ tone: 'ok', message: 'Item reactivated.' });
    },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not reactivate the item') }),
  });

  // Added on this PC and not made by the server yet (.docs/023): first on the first page.
  const pending = usePendingInPanel(
    'sku.create',
    (op) => { setFixOp(op); setForm('fix'); },
    (message) => setNotice({ tone: 'ok', message }),
  );
  const pendingItems = skip === 0
    ? findItems(pending.ops.map(pendingSku), search).filter((r) => !categoryId || r.categoryId === categoryId)
    : [];
  const rows = [...pendingItems, ...(data?.items ?? [])];
  const page = useOpenRecord(rows);
  const selected = rows.find((r) => r.id === selectedKey) ?? (page.row?.id === selectedKey ? page.row : null);

  function show(row: SkuRow | null) {
    if (row) {
      setSelectedKey(row.id);
      page.open(row);
    } else page.close();
    setNotice(null);
  }

  /** Deleted outright: nothing left to show. Switched off: still there, deleted. */
  function afterRetire(result: RetireResult) {
    if (result.outcome === 'deleted') page.close();
    else page.patch({ isActive: false });
  }

  const checkedRows = rows.filter((r) => checked.has(r.id));
  function toggleChecked(key: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function toggleCheckAll() {
    setChecked((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  }
  function selectRange(keys: string[]) {
    setChecked(new Set(keys));
  }

  const [bulkBusy, setBulkBusy] = useState(false);

  async function bulkRetire() {
    const targets = checkedRows.filter((r) => r.isActive);
    if (targets.length === 0) return;
    if (!window.confirm(`Delete ${targets.length} item(s)? An item never used is deleted; one with history is switched off.`)) return;
    setBulkBusy(true);
    let ok = 0;
    const failed: string[] = [];
    for (const row of targets) {
      try {
        await api.delete(`/api/v1/inventory/skus/${row.id}`);
        ok += 1;
      } catch (e) {
        failed.push(`${row.code} (${errorMessage(e, 'failed')})`);
      }
    }
    setBulkBusy(false);
    invalidate();
    setChecked(new Set());
    setNotice(
      failed.length === 0
        ? { tone: 'ok', message: `${ok} item(s) deleted.` }
        : { tone: 'error', message: `${ok} of ${targets.length} deleted. Failed: ${failed.join(', ')}` },
    );
  }

  async function bulkReactivate() {
    const targets = checkedRows.filter((r) => !r.isActive);
    if (targets.length === 0) return;
    setBulkBusy(true);
    let ok = 0;
    const failed: string[] = [];
    for (const row of targets) {
      try {
        await api.patch(`/api/v1/inventory/skus/${row.id}`, { isActive: true });
        ok += 1;
      } catch (e) {
        failed.push(`${row.code} (${errorMessage(e, 'failed')})`);
      }
    }
    setBulkBusy(false);
    invalidate();
    setChecked(new Set());
    setNotice(
      failed.length === 0
        ? { tone: 'ok', message: `${ok} item(s) reactivated.` }
        : { tone: 'error', message: `${ok} of ${targets.length} reactivated. Failed: ${failed.join(', ')}` },
    );
  }

  function rowActions(row: SkuRow): DesktopAction[] {
    const opener: DesktopAction = { label: 'Open', icon: 'details', shortcut: 'Enter', onSelect: () => show(row), allowed: true };
    const op = pending.opOf(row);
    if (op) return [opener, ...pending.actionsFor(op, can('inv.sku.create'))];
    return [opener, ...needsTheLine(serverRowActions(row), online)];
  }

  function serverRowActions(row: SkuRow): DesktopAction[] {
    return [
      { label: 'Edit', icon: 'edit', onSelect: () => { setSelectedKey(row.id); setForm('edit'); }, allowed: can('inv.sku.update') },
      row.isActive
        ? {
            label: 'Delete', icon: 'retire',
            onSelect: () => {
              if (
                window.confirm(
                  `Delete ${row.code}? If it was never used, it is deleted. If it was used, it is switched off and its history stays.`,
                )
              ) {
                setSelectedKey(row.id);
                retire.mutate(row.id);
              }
            },
            allowed: can('inv.sku.delete'),
            danger: true,
            separatorBefore: true,
          }
        : {
            label: 'Reactivate', icon: 'reactivate',
            onSelect: () => {
              setSelectedKey(row.id);
              reactivate.mutate(row.id);
            },
            allowed: can('inv.sku.update'),
            separatorBefore: true,
          },
    ];
  }

  const toolbarActions: DesktopAction[] = [
    { label: 'Add item', icon: 'add', primary: true, onSelect: () => setForm('create'), allowed: can('inv.sku.create') },
  ];

  const columns: Array<GridColumn<SkuRow>> = [
    { key: 'code', header: 'Code', width: 110, render: (r) => <span className="mono">{r.code}</span> },
    {
      key: 'name',
      header: 'Item',
      width: 240,
      render: (r) => (
        <>
          <AppIcon kind={categoryKind(r.categoryCode)} />{' '}
          {r.nameEn}
          {r.nameUr && <span className="urdu"> · {r.nameUr}</span>}
        </>
      ),
    },
    { key: 'category', header: 'Category', width: 140, render: (r) => <span className="dim withicon"><AppIcon kind={categoryKind(r.categoryCode)} size={14} />{r.category}</span> },
    { key: 'uom', header: 'Unit', width: 90, render: (r) => <span className="dim">{r.uom}</span> },
    { key: 'pack', header: 'Package', width: 160, render: (r) => <span className="dim">{defaultPackage(r)}</span> },
    {
      key: 'reorder',
      header: 'Reorder at',
      align: 'end',
      width: 100,
      render: (r) => (r.reorderLevel ? quantity(r.reorderLevel, r.uomPrecision) : '—'),
    },
    {
      key: 'tracking',
      header: 'Tracking',
      width: 130,
      render: (r) => (
        <>
          {r.isBatchTracked && <span className="pill">Lot</span>}
          {r.hasExpiry && <span className="pill">Expiry</span>}
          {!r.isBatchTracked && !r.hasExpiry && <span className="dim">—</span>}
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      render: (r) => {
        const op = pending.opOf(r);
        if (op) return pending.statusOf(op);
        return r.isActive ? <span className="pill">Active</span> : <span className="pill pill--alert">⚠ Deleted</span>;
      },
    },
  ];

  if (form) {
    return (
      <SkuForm
        key={form === 'edit' && selected ? selected.id : form === 'fix' ? `fix-${fixOp?.id}` : 'create'}
        initial={form === 'edit' ? (selected ?? undefined) : undefined}
        fixing={form === 'fix' ? (fixOp ?? undefined) : undefined}
        onCancel={() => setForm(null)}
        onSaved={(message) => {
          invalidate();
          // Put right and sent again, it is new work: back to the list to find it.
          if (form === 'fix') page.close();
          setNotice({ tone: 'ok', message });
          setForm(null);
        }}
      />
    );
  }

  const openItem = page.row;
  if (openItem && isPendingRow(openItem)) {
    return (
      <PendingRecordPage
        rowId={openItem.id}
        backLabel="Back to items"
        onBack={() => show(null)}
        onFix={(op) => { setFixOp(op); setForm('fix'); }}
        onOpen={(id) => show(rows.find((r) => r.id === id) ?? null)}
        onDone={(said) => { show(null); setNotice({ tone: 'ok', message: said }); }}
      />
    );
  }
  if (openItem) {
    return (
      <RecordPage
        backLabel="Back to items"
        onBack={() => show(null)}
        icon={<AppIcon kind={categoryKind(openItem.categoryCode)} size={18} />}
        code={openItem.code}
        name={openItem.nameEn}
        nameUr={openItem.nameUr}
        status={<MasterStatus row={{ isActive: openItem.isActive, needsReview: false }} />}
        actions={[
          ...rowActions(openItem),
          {
            label: 'See its stock', icon: 'stock',
            // The Stock screen, showing only this item, open on the stores that hold it.
            onSelect: () => useShellNav.getState().navigate('inv-workbench', { skuId: openItem.id, skuLabel: `${openItem.code} ${openItem.nameEn}` }),
            // Whoever may open the Stock screen at all.
            allowed: can('inv.dashboard.read') || can('inv.batch.read') || can('inv.ledger.read'),
          },
        ]}
        notice={notice}
      >
        <ItemPage row={openItem} />
      </RecordPage>
    );
  }

  return (
    <div className="workbench__main">
      <div className="filterbar">
        <SearchBar
          value={search}
          onChange={(v) => { setSearch(v); setSkip(0); setChecked(new Set()); }}
          placeholder="Search items by code or name…"
        />
        <Select
          label="Category"
          value={categoryId}
          onChange={(v) => { setCategoryId(v); setSkip(0); setChecked(new Set()); }}
          placeholder="All categories"
          options={(categories ?? []).map((c) => ({ value: c.id, label: c.nameEn }))}
        />
        <label className="settings__toggle">
          <input
            type="checkbox"
            checked={showRetired}
            onChange={(e) => { setShowRetired(e.target.checked); setSkip(0); setChecked(new Set()); }}
          />
          <span className="field__label">Show deleted</span>
        </label>
      </div>

      <ActionBar actions={toolbarActions} />

      {checked.size > 0 && (
        <div className="actionbar actionbar--bulk">
          <span className="actionbar__count">{checked.size} selected</span>
          {can('inv.sku.delete') && (
            <button type="button" className="btn btn--sm btn--withicon" disabled={bulkBusy} onClick={() => void bulkRetire()}>
              <AppIcon kind="retire" size={15} />
              Delete selected
            </button>
          )}
          {can('inv.sku.update') && (
            <button type="button" className="btn btn--sm btn--withicon" disabled={bulkBusy} onClick={() => void bulkReactivate()}>
              <AppIcon kind="reactivate" size={15} />
              Reactivate selected
            </button>
          )}
          <button type="button" className="btn btn--ghost btn--sm btn--withicon" onClick={() => setChecked(new Set())}>
            <AppIcon kind="clear" size={15} />
            Clear selection
          </button>
        </div>
      )}

      {isError && <ErrorNote message={error.message} />}
      {notice && (notice.tone === 'ok' ? <OkNote message={notice.message} /> : <ErrorNote message={notice.message} />)}

      <div className="workbench__main">
            <DataGrid
              id="reference-items"
              label="Items"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              // One tap opens it on its own screen; one added on this PC opens as what was saved.
              onOpen={show}
              openOnClick
              actions={rowActions}
              loading={isPending && !isOfflineGap(list)}
              empty={isOfflineGap(list) ? NOT_ON_THIS_PC : search || categoryId ? 'No items match your search.' : 'No items yet.'}
              selectedKeys={checked}
              onToggleSelect={toggleChecked}
              onToggleSelectAll={toggleCheckAll}
              onSelectRange={selectRange}
            />
            <Pager skip={skip} take={PAGE_SIZE} total={data?.total ?? 0} onChange={(s) => { setSkip(s); setChecked(new Set()); }} />
      </div>

    </div>
  );
}

// ------------------------------------------------- Categories and units

/*
 * A category or unit typed into the item form is added by name alone, so it
 * is marked for review until someone opens it here and saves it.
 */

function countToReview(rows: ReadonlyArray<{ isActive: boolean; needsReview: boolean }> | undefined) {
  return (rows ?? []).filter((r) => r.isActive && r.needsReview).length;
}

function MasterStatus({
  row,
  onReview,
}: {
  row: { isActive: boolean; needsReview: boolean };
  onReview?: (() => void) | undefined;
}) {
  if (!row.isActive) return <span className="pill pill--alert">⚠ Deleted</span>;
  if (row.needsReview) return <ReviewPill label="Needs review" onReview={onReview} />;
  return <span className="pill">Active</span>;
}

// -------------------------------------------------------------- Categories

/** A category's own screen. */
function CategoryPage({ row }: { row: CategoryRow }) {
  return (
    <>
      {!row.isActive && <DeletedNote what="category" />}
      {row.isActive && row.needsReview && (
        <p className="docpage__note" role="note">
          Added from the item form by its name alone. Check the details, then use Edit and save to confirm it.
        </p>
      )}
      <div className="tiles">
        <Tile label="Items in it" value={String(row.skuCount)} />
        <Tile label="Feed" value={row.isFeed ? 'Yes' : 'No'} note={row.isFeed ? 'Counts towards feed and FCR' : undefined} />
      </div>
      <section className="stack docpage__section">
        <h3 className="formsection">Category</h3>
        <CategoryDetails row={row} />
      </section>
    </>
  );
}

function CategoryDetails({ row }: { row: CategoryRow }) {
  return (
    <dl className="detaillist">
      <dt>Code</dt>
      <dd className="mono">{row.code}</dd>
      <dt>Name</dt>
      <dd>
        {row.nameEn}
        {row.nameUr && <span className="urdu"> · {row.nameUr}</span>}
      </dd>
      <dt>Items in this category</dt>
      <dd>{row.skuCount}</dd>
      <dt>Feed</dt>
      <dd>
        {row.isFeed ? (
          'Yes — issuing these to a shed counts towards the flock’s feed and its FCR'
        ) : (
          <span className="dim">No</span>
        )}
      </dd>
      <dt>Status</dt>
      <dd><MasterStatus row={row} /></dd>
    </dl>
  );
}

function CategoryForm({
  initial,
  fixing,
  onCancel,
  onSaved,
}: {
  initial?: CategoryRow | undefined;
  /** A category added on this PC that the server refused, being put right. */
  fixing?: Operation | undefined;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const editing = initial !== undefined;
  const reviewing = initial?.needsReview === true;
  const [form, setForm] = useState({
    nameEn: initial?.nameEn ?? bodyText(fixing, 'nameEn'),
    nameUr: initial?.nameUr ?? bodyText(fixing, 'nameUr'),
    isFeed: initial?.isFeed ?? bodyText(fixing, 'isFeed') === 'true',
  });

  const save = useMutation({
    mutationFn: (): Promise<ReferenceSaved> => {
      if (initial) {
        // Saving here is the review, even with nothing changed.
        return api
          .patch(`/api/v1/inventory/categories/${initial.id}`, {
            nameEn: form.nameEn.trim(),
            ...(form.nameUr.trim() ? { nameUr: form.nameUr.trim() } : {}),
            isFeed: form.isFeed,
            needsReview: false,
          })
          .then(() => ({ code: initial.code, waiting: false }));
      }
      // Through the outbox (.docs/023): made now with a line, kept on this PC without.
      const name = form.nameEn.trim();
      const body = { nameEn: name, ...(form.nameUr.trim() ? { nameUr: form.nameUr.trim() } : {}), isFeed: form.isFeed };
      if (fixing) return resendReference(fixing, body, `New category: ${name}`, name);
      return saveReference([createOp('category.create', '/api/v1/inventory/categories', body, `New category: ${name}`)], name);
    },
    onSuccess: (result) =>
      onSaved(
        editing
          ? `${result.code} updated.`
          : result.waiting
            ? `“${form.nameEn.trim()}” is saved on this PC. It gets its code when it is sent.`
            : `${result.code} added to the category list.`,
      ),
  });

  return (
    <FormPanel title={editing ? `Edit ${initial.code}` : fixing ? 'Put right a refused category' : 'Add a category'} icon={<AppIcon kind="category" size={18} />}>
      {fixing?.lastError && <ErrorNote message={fixing.lastError} />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {reviewing && (
          <ReviewBanner message="Added from the item form with just a name. Check the name, Urdu name and feed setting, then save to confirm." />
        )}
        <div className="formgrid formgrid--3">
          {editing && (
            <Field
              label="Code"
              mono
              value={initial.code}
              onChange={() => {}}
              disabled
              hint="Given when it was created, and never changes"
            />
          )}
          <Field label="Name" required value={form.nameEn} onChange={(nameEn) => setForm((f) => ({ ...f, nameEn }))} placeholder="Feed" />
          <Field label="Name (Urdu)" urdu value={form.nameUr} onChange={(nameUr) => setForm((f) => ({ ...f, nameUr }))} />
        </div>

        {/* A flag rather than a fixed list of codes: farms split feed into
            starter, grower and finisher categories of their own, and each of
            those is still feed when working out feed per bird. */}
        <label className="settings__toggle">
          <input
            type="checkbox"
            checked={form.isFeed}
            onChange={(e) => setForm((f) => ({ ...f, isFeed: e.target.checked }))}
          />
          <span className="field__label">
            These items are feed
            <span className="settings__hint">
              Issuing them to a shed counts towards that flock&rsquo;s feed and its FCR.
            </span>
          </span>
        </label>

        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

        <FormActions onCancel={onCancel} cancelLabel={editing ? 'Cancel' : 'Close'}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : reviewing ? 'Save and confirm' : editing ? 'Save changes' : 'Add category'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}

function CategoriesPanel({ fixOperation }: { fixOperation?: Operation | undefined }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [showRetired, setShowRetired] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [form, setForm] = useState<'create' | 'edit' | 'fix' | null>(fixOperation ? 'fix' : null);
  const [fixOp, setFixOp] = useState<Operation | null>(fixOperation ?? null);
  const online = useConnection((s) => s.online);
  const [notice, setNotice] = useState<Notice>(null);

  const list = useQuery({
    queryKey: ['inv', 'categories', 'reference', showRetired],
    queryFn: () => api.get('/api/v1/inventory/categories', { includeInactive: showRetired ? 'true' : undefined }),
  });
  const { data, isPending, isError, error } = list;

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['inv', 'categories'] });

  const retire = useMutation({
    mutationFn: (id: string) => api.delete<RetireResult>(`/api/v1/inventory/categories/${id}`),
    onSuccess: (result) => { invalidate(); afterRetire(result); setNotice({ tone: 'ok', message: result.message }); },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not delete the category') }),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => api.patch(`/api/v1/inventory/categories/${id}`, { isActive: true }),
    onSuccess: () => { invalidate(); page.patch({ isActive: true }); setNotice({ tone: 'ok', message: 'Category reactivated.' }); },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not reactivate the category') }),
  });

  // Added on this PC and not made by the server yet (.docs/023): first in the list.
  const pending = usePendingInPanel(
    'category.create',
    (op) => { setFixOp(op); setForm('fix'); },
    (message) => setNotice({ tone: 'ok', message }),
  );
  const rows = [...pending.ops.map(pendingCategory), ...(data ?? [])];
  const page = useOpenRecord<CategoryRow>(rows);
  const selected = rows.find((r) => r.id === selectedKey) ?? (page.row?.id === selectedKey ? page.row : null);

  function show(row: CategoryRow | null) {
    if (row) {
      setSelectedKey(row.id);
      page.open(row);
    } else page.close();
    setNotice(null);
  }

  /** Deleted outright: nothing left to show. Switched off: still there, deleted. */
  function afterRetire(result: RetireResult) {
    if (result.outcome === 'deleted') page.close();
    else page.patch({ isActive: false });
  }
  // Something still waiting on this PC is reviewed once the server has made it.
  const toReview = rows.filter((r: CategoryRow) => r.isActive && r.needsReview && !isPendingRow(r));
  const firstToReview = toReview[0];
  const review = can('inv.category.update') && online ? (row: CategoryRow) => { if (isPendingRow(row)) return; setSelectedKey(row.id); setForm('edit'); } : undefined;

  function rowActions(row: CategoryRow): DesktopAction[] {
    const opener: DesktopAction = { label: 'Open', icon: 'details', shortcut: 'Enter', onSelect: () => show(row), allowed: true };
    const op = pending.opOf(row);
    if (op) return [opener, ...pending.actionsFor(op, can('inv.category.create'))];
    return [opener, ...needsTheLine(serverRowActions(row), online)];
  }

  function serverRowActions(row: CategoryRow): DesktopAction[] {
    return [
      { label: 'Edit', icon: 'edit', onSelect: () => { setSelectedKey(row.id); setForm('edit'); }, allowed: can('inv.category.update') },
      row.isActive
        ? {
            label: 'Delete', icon: 'retire',
            onSelect: () => {
              const warn =
                row.skuCount > 0
                  ? `${row.skuCount} item(s) are in this category, so it will be switched off rather than deleted — its history and those items are unaffected.`
                  : 'It was never used, so it is deleted outright.';
              if (window.confirm(`Delete ${row.code}? ${warn}`)) {
                setSelectedKey(row.id);
                retire.mutate(row.id);
              }
            },
            allowed: can('inv.category.delete'),
            danger: true,
            separatorBefore: true,
          }
        : { label: 'Reactivate', icon: 'reactivate', onSelect: () => { setSelectedKey(row.id); reactivate.mutate(row.id); }, allowed: can('inv.category.update'), separatorBefore: true },
    ];
  }

  const toolbarActions: DesktopAction[] = [
    { label: 'Add category', icon: 'add', primary: true, onSelect: () => setForm('create'), allowed: can('inv.category.create') },
  ];

  const columns: Array<GridColumn<CategoryRow>> = [
    { key: 'code', header: 'Code', width: 110, render: (r) => <span className="mono">{r.code}</span> },
    {
      key: 'name',
      header: 'Category',
      width: 240,
      render: (r) => (
        <>
          <AppIcon kind={categoryKind(r.code)} />{' '}
          {r.nameEn}
          {r.nameUr && <span className="urdu"> · {r.nameUr}</span>}
        </>
      ),
    },
    { key: 'items', header: 'Items', align: 'end', width: 90, render: (r) => r.skuCount },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      render: (r) => {
        const op = pending.opOf(r);
        return op ? pending.statusOf(op) : <MasterStatus row={r} onReview={review && (() => review(r))} />;
      },
    },
  ];

  // Edit opens over the list or over the entry's own screen.
  const formModal = form && (
    <Modal title={form === 'edit' && selected ? `Edit ${selected.code}` : 'Add a category'} onClose={() => setForm(null)}>
      <CategoryForm
        key={form === 'edit' && selected ? selected.id : form === 'fix' ? `fix-${fixOp?.id}` : 'create'}
        initial={form === 'edit' ? (selected ?? undefined) : undefined}
        fixing={form === 'fix' ? (fixOp ?? undefined) : undefined}
        onCancel={() => setForm(null)}
        onSaved={(message) => { invalidate(); if (form === 'fix') page.close(); setNotice({ tone: 'ok', message }); setForm(null); }}
      />
    </Modal>
  );

  const openRow = page.row;
  if (openRow && isPendingRow(openRow)) {
    return (
      <>
        <PendingRecordPage
          rowId={openRow.id}
          backLabel="Back to categories"
          onBack={() => show(null)}
          onFix={(op) => { setFixOp(op); setForm('fix'); }}
          onOpen={(id) => show(rows.find((r) => r.id === id) ?? null)}
          onDone={(said) => { show(null); setNotice({ tone: 'ok', message: said }); }}
        />
        {formModal}
      </>
    );
  }
  if (openRow) {
    return (
      <>
        <RecordPage
          backLabel="Back to categories"
          onBack={() => show(null)}
          icon={<AppIcon kind={categoryKind(openRow.code)} size={18} />}
          code={openRow.code}
          name={openRow.nameEn}
          nameUr={openRow.nameUr}
          status={<MasterStatus row={openRow} onReview={review && (() => review(openRow))} />}
          actions={rowActions(openRow)}
          notice={notice}
        >
          <CategoryPage row={openRow} />
        </RecordPage>
        {formModal}
      </>
    );
  }

  return (
    <div className="workbench__main">
      <div className="filterbar">
        <label className="settings__toggle">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
          <span className="field__label">Show deleted</span>
        </label>
      </div>

      <ActionBar actions={toolbarActions} />

      {isError && <ErrorNote message={error.message} />}
      {notice && (notice.tone === 'ok' ? <OkNote message={notice.message} /> : <ErrorNote message={notice.message} />)}
      {firstToReview && (
        <ReviewBanner
          message={`${toReview.length} ${toReview.length === 1 ? 'category was' : 'categories were'} added from the item form. Check the details and save to confirm.`}
          onReview={review && (() => review(firstToReview))}
        />
      )}

      <DataGrid
        id="reference-categories"
        label="Categories"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selectedKey}
        onSelect={setSelectedKey}
        // One tap opens it on its own screen.
        onOpen={show}
        openOnClick
        actions={rowActions}
        loading={isPending && !isOfflineGap(list)}
        empty="No categories yet."
      />

      {formModal}
    </div>
  );
}

// ------------------------------------------------------------------ Units

/**
 * A unit added from the item form was given a stand-in precision, so it reads
 * as not set until someone reviews it and chooses.
 */
function CountedIn({ row, onReview }: { row: UomRow; onReview?: (() => void) | undefined }) {
  if (row.needsReview) return <ReviewPill label="Not set" onReview={onReview} />;
  return <>{PRECISION_OPTIONS.find((p) => Number(p.value) === row.precision)?.label ?? `${row.precision} decimal place(s)`}</>;
}

/** A unit's own screen. */
function UnitPage({ row }: { row: UomRow }) {
  return (
    <>
      {!row.isActive && <DeletedNote what="unit" />}
      {row.isActive && row.needsReview && (
        <p className="docpage__note" role="note">
          Added from the item form by its name alone. Use Edit to choose how it is counted, then save to confirm it.
        </p>
      )}
      <div className="tiles">
        <Tile label="Abbreviation" value={row.abbreviation ?? '—'} />
        <Tile label="Items using it" value={String(row.skuCount)} />
      </div>
      <section className="stack docpage__section">
        <h3 className="formsection">Unit</h3>
        <UnitDetails row={row} />
      </section>
    </>
  );
}

function UnitDetails({ row }: { row: UomRow }) {
  return (
    <dl className="detaillist">
      <dt>Code</dt>
      <dd className="mono">{row.code}</dd>
      <dt>Name</dt>
      <dd>
        {row.nameEn}
        {row.nameUr && <span className="urdu"> · {row.nameUr}</span>}
      </dd>
      <dt>Abbreviation</dt>
      <dd>{row.abbreviation ?? <span className="dim">—</span>}</dd>
      <dt>Counted in</dt>
      <dd><CountedIn row={row} /></dd>
      <dt>Items using this unit</dt>
      <dd>{row.skuCount}</dd>
      <dt>Status</dt>
      <dd><MasterStatus row={row} /></dd>
    </dl>
  );
}

function UnitForm({
  initial,
  fixing,
  onCancel,
  onSaved,
}: {
  initial?: UomRow | undefined;
  /** A unit added on this PC that the server refused, being put right. */
  fixing?: Operation | undefined;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const editing = initial !== undefined;
  const reviewing = initial?.needsReview === true;
  const [form, setForm] = useState({
    nameEn: initial?.nameEn ?? bodyText(fixing, 'nameEn'),
    nameUr: initial?.nameUr ?? bodyText(fixing, 'nameUr'),
    abbreviation: initial?.abbreviation ?? bodyText(fixing, 'abbreviation'),
    // Under review the stored precision is only a stand-in, so it starts blank
    // and must be chosen before the unit is confirmed.
    precision: reviewing ? '' : initial?.precision?.toString() ?? (bodyText(fixing, 'precision') || '0'),
  });

  const save = useMutation({
    mutationFn: (): Promise<ReferenceSaved> => {
      if (initial) {
        // Saving here is the review, even with nothing changed.
        return api
          .patch(`/api/v1/inventory/uoms/${initial.id}`, {
            nameEn: form.nameEn.trim(),
            ...(form.nameUr.trim() ? { nameUr: form.nameUr.trim() } : {}),
            abbreviation: form.abbreviation.trim(),
            precision: Number(form.precision),
            needsReview: false,
          })
          .then(() => ({ code: initial.code, waiting: false }));
      }
      // Through the outbox (.docs/023): made now with a line, kept on this PC without.
      const name = form.nameEn.trim();
      const body = {
        nameEn: name,
        ...(form.nameUr.trim() ? { nameUr: form.nameUr.trim() } : {}),
        ...(form.abbreviation.trim() ? { abbreviation: form.abbreviation.trim() } : {}),
        precision: Number(form.precision),
      };
      if (fixing) return resendReference(fixing, body, `New unit: ${name}`, name);
      return saveReference([createOp('uom.create', '/api/v1/inventory/uoms', body, `New unit: ${name}`)], name);
    },
    onSuccess: (result) =>
      onSaved(
        editing
          ? `${result.code} updated.`
          : result.waiting
            ? `“${form.nameEn.trim()}” is saved on this PC. It gets its code when it is sent.`
            : `${result.code} added to the unit list.`,
      ),
  });

  return (
    <FormPanel title={editing ? `Edit ${initial.code}` : fixing ? 'Put right a refused unit' : 'Add a unit'} icon={<AppIcon kind="unit" size={18} />}>
      {fixing?.lastError && <ErrorNote message={fixing.lastError} />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {reviewing && (
          <ReviewBanner message="Added from the item form with just a name. Choose how it is counted and add an abbreviation, then save to confirm." />
        )}
        <div className="formgrid formgrid--4">
          {editing && (
            <Field
              label="Code"
              mono
              value={initial.code}
              onChange={() => {}}
              disabled
              hint="Given when it was created, and never changes"
            />
          )}
          <Field label="Name" required value={form.nameEn} onChange={(nameEn) => setForm((f) => ({ ...f, nameEn }))} placeholder="Kilogram" />
          <Field label="Name (Urdu)" urdu value={form.nameUr} onChange={(nameUr) => setForm((f) => ({ ...f, nameUr }))} />
          <Field
            label="Abbreviation"
            value={form.abbreviation}
            onChange={(abbreviation) => setForm((f) => ({ ...f, abbreviation }))}
            placeholder="kg"
          />
          <Select
            label="Counted in"
            required
            value={form.precision}
            onChange={(precision) => setForm((f) => ({ ...f, precision }))}
            options={PRECISION_OPTIONS}
            hint={reviewing ? 'Bags and vials are whole numbers; kg and litres usually take 2 decimals' : undefined}
          />
        </div>

        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

        <FormActions onCancel={onCancel} cancelLabel={editing ? 'Cancel' : 'Close'}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : reviewing ? 'Save and confirm' : editing ? 'Save changes' : 'Add unit'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}

function UnitsPanel({ fixOperation }: { fixOperation?: Operation | undefined }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [showRetired, setShowRetired] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [form, setForm] = useState<'create' | 'edit' | 'fix' | null>(fixOperation ? 'fix' : null);
  const [fixOp, setFixOp] = useState<Operation | null>(fixOperation ?? null);
  const online = useConnection((s) => s.online);
  const [notice, setNotice] = useState<Notice>(null);

  const list = useQuery({
    queryKey: ['inv', 'uoms', 'reference', showRetired],
    queryFn: () => api.get('/api/v1/inventory/uoms', { includeInactive: showRetired ? 'true' : undefined }),
  });
  const { data, isPending, isError, error } = list;

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['inv', 'uoms'] });

  const retire = useMutation({
    mutationFn: (id: string) => api.delete<RetireResult>(`/api/v1/inventory/uoms/${id}`),
    onSuccess: (result) => { invalidate(); afterRetire(result); setNotice({ tone: 'ok', message: result.message }); },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not delete the unit') }),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => api.patch(`/api/v1/inventory/uoms/${id}`, { isActive: true }),
    onSuccess: () => { invalidate(); page.patch({ isActive: true }); setNotice({ tone: 'ok', message: 'Unit reactivated.' }); },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not reactivate the unit') }),
  });

  // Added on this PC and not made by the server yet (.docs/023): first in the list.
  const pending = usePendingInPanel(
    'uom.create',
    (op) => { setFixOp(op); setForm('fix'); },
    (message) => setNotice({ tone: 'ok', message }),
  );
  const rows = [...pending.ops.map(pendingUom), ...(data ?? [])];
  const page = useOpenRecord<UomRow>(rows);
  const selected = rows.find((r) => r.id === selectedKey) ?? (page.row?.id === selectedKey ? page.row : null);

  function show(row: UomRow | null) {
    if (row) {
      setSelectedKey(row.id);
      page.open(row);
    } else page.close();
    setNotice(null);
  }

  /** Deleted outright: nothing left to show. Switched off: still there, deleted. */
  function afterRetire(result: RetireResult) {
    if (result.outcome === 'deleted') page.close();
    else page.patch({ isActive: false });
  }
  // Something still waiting on this PC is reviewed once the server has made it.
  const toReview = rows.filter((r: UomRow) => r.isActive && r.needsReview && !isPendingRow(r));
  const firstToReview = toReview[0];
  const review = can('inv.uom.update') && online ? (row: UomRow) => { if (isPendingRow(row)) return; setSelectedKey(row.id); setForm('edit'); } : undefined;

  function rowActions(row: UomRow): DesktopAction[] {
    const opener: DesktopAction = { label: 'Open', icon: 'details', shortcut: 'Enter', onSelect: () => show(row), allowed: true };
    const op = pending.opOf(row);
    if (op) return [opener, ...pending.actionsFor(op, can('inv.uom.create'))];
    return [opener, ...needsTheLine(serverRowActions(row), online)];
  }

  function serverRowActions(row: UomRow): DesktopAction[] {
    return [
      { label: 'Edit', icon: 'edit', onSelect: () => { setSelectedKey(row.id); setForm('edit'); }, allowed: can('inv.uom.update') },
      row.isActive
        ? {
            label: 'Delete', icon: 'retire',
            onSelect: () => {
              const warn =
                row.skuCount > 0
                  ? `${row.skuCount} item(s) are counted in this unit, so it will be switched off rather than deleted — its history and those items are unaffected.`
                  : 'It was never used, so it is deleted outright.';
              if (window.confirm(`Delete ${row.code}? ${warn}`)) {
                setSelectedKey(row.id);
                retire.mutate(row.id);
              }
            },
            allowed: can('inv.uom.delete'),
            danger: true,
            separatorBefore: true,
          }
        : { label: 'Reactivate', icon: 'reactivate', onSelect: () => { setSelectedKey(row.id); reactivate.mutate(row.id); }, allowed: can('inv.uom.update'), separatorBefore: true },
    ];
  }

  const toolbarActions: DesktopAction[] = [
    { label: 'Add unit', icon: 'add', primary: true, onSelect: () => setForm('create'), allowed: can('inv.uom.create') },
  ];

  const columns: Array<GridColumn<UomRow>> = [
    { key: 'code', header: 'Code', width: 110, render: (r) => <span className="mono">{r.code}</span> },
    {
      key: 'name',
      header: 'Unit',
      width: 220,
      render: (r) => (
        <>
          <AppIcon kind={'unit'} />{' '}
          {r.nameEn}
          {r.abbreviation && <span className="dim"> ({r.abbreviation})</span>}
          {r.nameUr && <span className="urdu"> · {r.nameUr}</span>}
        </>
      ),
    },
    { key: 'precision', header: 'Counted in', width: 190, render: (r) => <span className={r.needsReview ? undefined : 'dim'}><CountedIn row={r} onReview={review && (() => review(r))} /></span> },
    { key: 'items', header: 'Items', align: 'end', width: 90, render: (r) => r.skuCount },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      render: (r) => {
        const op = pending.opOf(r);
        return op ? pending.statusOf(op) : <MasterStatus row={r} onReview={review && (() => review(r))} />;
      },
    },
  ];

  // Edit opens over the list or over the entry's own screen.
  const formModal = form && (
    <Modal title={form === 'edit' && selected ? `Edit ${selected.code}` : 'Add a unit'} onClose={() => setForm(null)}>
      <UnitForm
        key={form === 'edit' && selected ? selected.id : form === 'fix' ? `fix-${fixOp?.id}` : 'create'}
        initial={form === 'edit' ? (selected ?? undefined) : undefined}
        fixing={form === 'fix' ? (fixOp ?? undefined) : undefined}
        onCancel={() => setForm(null)}
        onSaved={(message) => { invalidate(); if (form === 'fix') page.close(); setNotice({ tone: 'ok', message }); setForm(null); }}
      />
    </Modal>
  );

  const openRow = page.row;
  if (openRow && isPendingRow(openRow)) {
    return (
      <>
        <PendingRecordPage
          rowId={openRow.id}
          backLabel="Back to units"
          onBack={() => show(null)}
          onFix={(op) => { setFixOp(op); setForm('fix'); }}
          onOpen={(id) => show(rows.find((r) => r.id === id) ?? null)}
          onDone={(said) => { show(null); setNotice({ tone: 'ok', message: said }); }}
        />
        {formModal}
      </>
    );
  }
  if (openRow) {
    return (
      <>
        <RecordPage
          backLabel="Back to units"
          onBack={() => show(null)}
          icon={<AppIcon kind="unit" size={18} />}
          code={openRow.code}
          name={openRow.nameEn}
          nameUr={openRow.nameUr}
          status={<MasterStatus row={openRow} onReview={review && (() => review(openRow))} />}
          actions={rowActions(openRow)}
          notice={notice}
        >
          <UnitPage row={openRow} />
        </RecordPage>
        {formModal}
      </>
    );
  }

  return (
    <div className="workbench__main">
      <div className="filterbar">
        <label className="settings__toggle">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
          <span className="field__label">Show deleted</span>
        </label>
      </div>

      <ActionBar actions={toolbarActions} />

      {isError && <ErrorNote message={error.message} />}
      {notice && (notice.tone === 'ok' ? <OkNote message={notice.message} /> : <ErrorNote message={notice.message} />)}
      {firstToReview && (
        <ReviewBanner
          message={`${toReview.length} ${toReview.length === 1 ? 'unit was' : 'units were'} added from the item form. Choose how each is counted, then save to confirm.`}
          onReview={review && (() => review(firstToReview))}
        />
      )}

      <DataGrid
        id="reference-units"
        label="Units"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selectedKey}
        onSelect={setSelectedKey}
        // One tap opens it on its own screen.
        onOpen={show}
        openOnClick
        actions={rowActions}
        loading={isPending && !isOfflineGap(list)}
        empty="No units yet."
      />

      {formModal}
    </div>
  );
}

// ----------------------------------------------------------------- Stores

/** A store's own screen: what it holds and what is running short, then its bins. */
function StorePage({ row }: { row: StoreRow }) {
  return (
    <>
      {!row.isActive && <DeletedNote what="store" />}
      <div className="tiles">
        <Tile label="Type" value={row.type === 'CENTRAL' ? 'Main store' : 'Farm store'} />
        <Tile label="Items in stock" value={String(row.itemsInStock)} />
        <Tile label="Below reorder" value={String(row.belowReorderCount)} tone={row.belowReorderCount > 0 ? 'warn' : undefined} />
        <Tile label="Bins" value={String(row.binCount)} />
      </div>
      <section className="stack docpage__section">
        <h3 className="formsection">Store</h3>
        <StoreDetails row={row} />
      </section>
      <section className="stack docpage__section">
        <h3 className="formsection">Bins</h3>
        <StoreBinsTab key={row.id} store={row} />
      </section>
    </>
  );
}

function StoreDetails({ row }: { row: StoreRow }) {
  return (
    <dl className="detaillist">
      <dt>Code</dt>
      <dd className="mono">{row.code}</dd>
      <dt>Name</dt>
      <dd>
        {row.nameEn}
        {row.nameUr && <span className="urdu"> · {row.nameUr}</span>}
      </dd>
      <dt>Type</dt>
      <dd>{row.type === 'CENTRAL' ? 'Main' : 'Farm'}</dd>
      <dt>Farm</dt>
      <dd>{row.farmName ? <>{row.farmCode} — {row.farmName}</> : '—'}</dd>
      <dt>Items held</dt>
      <dd>{row.skuCount}</dd>
      <dt>Bins</dt>
      <dd>{row.binCount}</dd>
      <dt>Items in stock now</dt>
      <dd>{row.itemsInStock}</dd>
      <dt>Below reorder</dt>
      <dd>{row.belowReorderCount}</dd>
      <dt>Last moved</dt>
      <dd>{row.lastMovementAt ? shortDate(row.lastMovementAt) : '—'}</dd>
      <dt>Status</dt>
      <dd>{row.isActive ? <span className="pill">Active</span> : <span className="pill pill--alert">⚠ Deleted</span>}</dd>
    </dl>
  );
}

function StoreForm({
  initial,
  onCancel,
  onSaved,
}: {
  initial?: StoreRow | undefined;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const editing = initial !== undefined;
  const [form, setForm] = useState({
    nameEn: initial?.nameEn ?? '',
    nameUr: initial?.nameUr ?? '',
    type: initial?.type ?? 'CENTRAL',
    farmId: initial?.farmId ?? '',
    /** The name of a farm typed in as new, while `farmId` is `NEW_FARM`. */
    newFarmName: '',
  });
  const [problem, setProblem] = useState<string | null>(null);

  // Deleted farms come too, only so a new farm cannot quietly reuse the name
  // or code of one that is merely switched off. Only active farms can be chosen.
  const farms = useQuery({
    queryKey: ['farms', 'list', true],
    queryFn: () => api.get('/api/v1/farms', { includeInactive: 'true' }),
  });
  const allFarms: FarmRow[] = farms.data ?? [];

  const canAddFarm = can('mdm.farm.create');
  const newFarm = form.type === 'FARM' && form.farmId === NEW_FARM;
  // The code a new farm is about to get. Not reserved: the save message
  // gives the one it really got.
  const farmCode = useCodePreview('Farm', newFarm);
  const deletedTwin = newFarm
    ? allFarms.find((f) => !f.isActive && sameFarmName(f.nameEn, form.newFarmName))
    : undefined;

  function chooseFarm(farmId: string) {
    setProblem(null);
    setForm((f) => ({ ...f, farmId, newFarmName: '' }));
  }

  /** Farm names are unique, so a typed name that is an existing farm's in all
   *  but case, spacing or punctuation picks that farm rather than adding a
   *  second one under it. */
  function addFarm(name: string) {
    const existing = allFarms.find((f) => f.isActive && sameFarmName(f.nameEn, name));
    if (existing) return chooseFarm(existing.id);
    setProblem(null);
    setForm((f) => ({ ...f, farmId: NEW_FARM, newFarmName: name }));
  }

  const save = useMutation({
    mutationFn: async (): Promise<{ code: string; farm?: { code: string; nameEn: string } }> => {
      const nameEn = form.nameEn.trim();
      const nameUr = form.nameUr.trim();
      if (initial) {
        await api.patch('/api/v1/stores/' + initial.id, { nameEn, ...(nameUr ? { nameUr } : {}) });
        return { code: initial.code };
      }
      // Creating a farm always creates its store, so a new farm and this store
      // are one request: posting the store as well would leave the farm with
      // two, and one transaction leaves nothing half-made if it fails.
      if (newFarm) {
        const farmName = form.newFarmName.trim();
        const created = await api.post<FarmCreated>('/api/v1/farms', {
          nameEn: farmName,
          storeName: nameEn,
          ...(nameUr ? { storeNameUr: nameUr } : {}),
          createShed: false,
          // Only its name was given here, so Farm Manager lists it for review.
          needsReview: true,
        });
        return { code: created.store.code, farm: { code: created.code, nameEn: farmName } };
      }
      return api.post<{ id: string; code: string }>('/api/v1/stores', {
        nameEn,
        ...(nameUr ? { nameUr } : {}),
        type: form.type,
        ...(form.type === 'FARM' && form.farmId ? { farmId: form.farmId } : {}),
      });
    },
    onSuccess: (result) => {
      if (result.farm) void queryClient.invalidateQueries({ queryKey: ['farms'] });
      onSaved(
        editing
          ? `${result.code} updated.`
          : result.farm
            ? `${result.code} added, with new farm ${result.farm.code} “${result.farm.nameEn}”. It is listed in Farm Manager to review.`
            : `${result.code} added to the store list.`,
      );
    },
  });

  function submit() {
    const issue =
      form.type !== 'FARM'
        ? null
        : !form.farmId
          ? canAddFarm
            ? 'Choose a farm by its code or name, or type a new name to add one.'
            : 'Choose the farm this store belongs to.'
          : deletedTwin
            ? deletedFarmMessage(deletedTwin)
            : null;
    setProblem(issue);
    if (!issue) save.mutate();
  }

  function deletedFarmMessage(farm: FarmRow) {
    return `${farm.code} “${farm.nameEn}” is deleted under this name. Reactivate it in Farm Manager, or use a different name.`;
  }

  return (
    <FormPanel title={editing ? `Edit ${initial.code}` : 'Add a store'} icon={<AppIcon kind="store" size={18} />}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div className="formgrid formgrid--3">
          {editing && (
            <Field
              label="Code"
              mono
              value={initial.code}
              onChange={() => {}}
              disabled
              hint="Given when it was created, and never changes"
            />
          )}
          <Field label="Name" required value={form.nameEn} onChange={(nameEn) => setForm((f) => ({ ...f, nameEn }))} />
          <Field label="Name (Urdu)" urdu value={form.nameUr} onChange={(nameUr) => setForm((f) => ({ ...f, nameUr }))} />
          <Select
            label="Type"
            value={form.type}
            onChange={(type) => setForm((f) => ({ ...f, type: type as 'CENTRAL' | 'FARM' }))}
            disabled={editing}
            hint={editing ? 'Cannot be changed once created' : undefined}
            options={[
              { value: 'CENTRAL', label: 'Main store' },
              { value: 'FARM', label: 'Farm store' },
            ]}
            placeholder=""
          />
          {form.type === 'FARM' && (
            <FarmPicker
              span={2}
              required
              value={form.farmId}
              onChange={chooseFarm}
              farms={farms.isError ? [] : farms.data}
              newFarm={{ name: form.newFarmName, code: farmCode.data?.code }}
              onCreate={canAddFarm && !editing ? addFarm : undefined}
              fallback={initial?.farmCode && initial.farmName ? { code: initial.farmCode, name: initial.farmName } : undefined}
              disabled={editing}
              hint={
                editing
                  ? 'Cannot be changed once created'
                  : deletedTwin
                    ? deletedFarmMessage(deletedTwin)
                    : !form.farmId
                      ? canAddFarm
                        ? 'Type a code or a name. A name not in the list can be added as a new farm.'
                        : 'Type a code or a name'
                      : undefined
              }
            />
          )}
        </div>

        {farms.isError && <ErrorNote message={errorMessage(farms.error, 'Could not load the farms')} />}
        {problem && <ErrorNote message={problem} />}
        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

        <FormActions onCancel={onCancel} cancelLabel={editing ? 'Cancel' : 'Close'}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add store'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}

function StoresPanel() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [form, setForm] = useState<'create' | 'edit' | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  // A store is a farm's own setup: added and changed with the line only.
  const online = useConnection((s) => s.online);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['stores', 'reference', showRetired],
    queryFn: () => api.get('/api/v1/stores', { includeInactive: showRetired ? 'true' : undefined }),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['stores'] });

  const retire = useMutation({
    mutationFn: (id: string) => api.delete<RetireResult>(`/api/v1/stores/${id}`),
    onSuccess: (result) => { invalidate(); afterRetire(result); setNotice({ tone: 'ok', message: result.message }); },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not delete the store') }),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => api.patch(`/api/v1/stores/${id}`, { isActive: true }),
    onSuccess: () => { invalidate(); page.patch({ isActive: true }); setNotice({ tone: 'ok', message: 'Store reactivated.' }); },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not reactivate the store') }),
  });

  const all = data ?? [];
  const rows = search
    ? all.filter(
        (s: StoreRow) =>
          s.code.toLowerCase().includes(search.toLowerCase()) || s.nameEn.toLowerCase().includes(search.toLowerCase()),
      )
    : all;
  const page = useOpenRecord<StoreRow>(rows);
  const selected = rows.find((r) => r.id === selectedKey) ?? (page.row?.id === selectedKey ? page.row : null);

  function show(row: StoreRow | null) {
    if (row) {
      setSelectedKey(row.id);
      page.open(row);
    } else page.close();
    setNotice(null);
  }

  /** Deleted outright: nothing left to show. Switched off: still there, deleted. */
  function afterRetire(result: RetireResult) {
    if (result.outcome === 'deleted') page.close();
    else page.patch({ isActive: false });
  }

  function rowActions(row: StoreRow): DesktopAction[] {
    const opener: DesktopAction = { label: 'Open', icon: 'details', shortcut: 'Enter', onSelect: () => show(row), allowed: true };
    return [opener, ...needsTheLine(serverRowActions(row), online)];
  }

  function serverRowActions(row: StoreRow): DesktopAction[] {
    return [
      { label: 'Edit', icon: 'edit', onSelect: () => { setSelectedKey(row.id); setForm('edit'); }, allowed: can('inv.store.update') },
      row.isActive
        ? {
            label: 'Delete', icon: 'retire',
            onSelect: () => {
              if (window.confirm(`Delete ${row.code}? If it was never used, it is deleted. If it was used, it is switched off and its history stays.`)) {
                setSelectedKey(row.id);
                retire.mutate(row.id);
              }
            },
            allowed: can('inv.store.delete'),
            danger: true,
            separatorBefore: true,
          }
        : { label: 'Reactivate', icon: 'reactivate', onSelect: () => { setSelectedKey(row.id); reactivate.mutate(row.id); }, allowed: can('inv.store.update'), separatorBefore: true },
    ];
  }

  const toolbarActions: DesktopAction[] = [
    ...needsTheLine([{ label: 'Add store', icon: 'add', primary: true, onSelect: () => setForm('create'), allowed: can('inv.store.create') }], online, { evenAdding: true }),
  ];

  const columns: Array<GridColumn<StoreRow>> = [
    { key: 'code', header: 'Code', width: 110, render: (r) => <span className="mono">{r.code}</span> },
    { key: 'name', header: 'Store', width: 220, render: (r) => <span className="withicon"><AppIcon kind={r.type === 'CENTRAL' ? 'centralStore' : 'store'} />{r.nameEn}</span> },
    { key: 'type', header: 'Type', width: 100, render: (r) => <span className="dim">{r.type === 'CENTRAL' ? 'Main' : 'Farm'}</span> },
    { key: 'farm', header: 'Farm', width: 140, render: (r) => <span className="dim">{r.farmCode ?? '—'}</span> },
    { key: 'items', header: 'Items held', align: 'end', width: 100, render: (r) => r.skuCount },
    { key: 'bins', header: 'Bins', align: 'end', width: 80, render: (r) => r.binCount },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      render: (r) => (r.isActive ? <span className="pill">Active</span> : <span className="pill pill--alert">⚠ Deleted</span>),
    },
  ];

  // Edit opens over the list or over the entry's own screen.
  const formModal = form && (
    <Modal title={form === 'edit' && selected ? `Edit ${selected.code}` : 'Add a store'} onClose={() => setForm(null)}>
      <StoreForm
        key={form === 'edit' && selected ? selected.id : 'create'}
        initial={form === 'edit' ? (selected ?? undefined) : undefined}
        onCancel={() => setForm(null)}
        onSaved={(message) => { invalidate(); setNotice({ tone: 'ok', message }); setForm(null); }}
      />
    </Modal>
  );

  const openRow = page.row;
  if (openRow) {
    return (
      <>
        <RecordPage
          backLabel="Back to stores"
          onBack={() => show(null)}
          icon={<AppIcon kind={openRow.type === 'CENTRAL' ? 'centralStore' : 'store'} size={18} />}
          code={openRow.code}
          name={openRow.nameEn}
          nameUr={openRow.nameUr}
          status={<MasterStatus row={{ isActive: openRow.isActive, needsReview: false }} />}
          actions={rowActions(openRow)}
          notice={notice}
        >
          <StorePage row={openRow} />
        </RecordPage>
        {formModal}
      </>
    );
  }

  return (
    <div className="workbench__main">
      <div className="filterbar">
        <SearchBar value={search} onChange={setSearch} placeholder="Search stores by code or name…" />
        <label className="settings__toggle">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
          <span className="field__label">Show deleted</span>
        </label>
      </div>

      <ActionBar actions={toolbarActions} />

      {isError && <ErrorNote message={error.message} />}
      {notice && (notice.tone === 'ok' ? <OkNote message={notice.message} /> : <ErrorNote message={notice.message} />)}

      <DataGrid
        id="reference-stores"
        label="Stores"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selectedKey}
        onSelect={setSelectedKey}
        // One tap opens it on its own screen.
        onOpen={show}
        openOnClick
        actions={rowActions}
        loading={isPending}
        empty={search ? 'No stores match your search.' : 'No stores in your scope.'}
      />

      {formModal}
    </div>
  );
}

// -------------------------------------------------------------- Suppliers

/** A supplier's own screen. */
function SupplierPage({ row }: { row: SupplierRow }) {
  return (
    <>
      {!row.isActive && <DeletedNote what="supplier" />}
      <section className="stack docpage__section">
        <h3 className="formsection">Supplier</h3>
        <SupplierDetails row={row} />
      </section>
    </>
  );
}

function SupplierDetails({ row }: { row: SupplierRow }) {
  return (
    <dl className="detaillist">
      <dt>Code</dt>
      <dd className="mono">{row.code}</dd>
      <dt>Name</dt>
      <dd>
        {row.nameEn}
        {row.nameUr && <span className="urdu"> · {row.nameUr}</span>}
      </dd>
      <dt>Contact</dt>
      <dd>{row.contactPerson ?? '—'}{row.contactNo ? ` · ${row.contactNo}` : ''}</dd>
      <dt>Alt. contact</dt>
      <dd>{row.altContactNo ?? '—'}</dd>
      <dt>Email</dt>
      <dd>{row.email ?? '—'}</dd>
      <dt>Address</dt>
      <dd>{row.address ?? '—'}{row.city ? `, ${row.city}` : ''}</dd>
      <dt>Notes</dt>
      <dd>{row.notes ?? '—'}</dd>
      <dt>Status</dt>
      <dd>{row.isActive ? <span className="pill">Active</span> : <span className="pill pill--alert">⚠ Deleted</span>}</dd>
    </dl>
  );
}

function SupplierForm({
  initial,
  fixing,
  onCancel,
  onSaved,
}: {
  initial?: SupplierRow | undefined;
  /** A supplier added on this PC that the server refused, being put right. */
  fixing?: Operation | undefined;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const editing = initial !== undefined;
  const [form, setForm] = useState({
    nameEn: initial?.nameEn ?? bodyText(fixing, 'nameEn'),
    nameUr: initial?.nameUr ?? bodyText(fixing, 'nameUr'),
    contactPerson: initial?.contactPerson ?? bodyText(fixing, 'contactPerson'),
    contactNo: initial?.contactNo ?? bodyText(fixing, 'contactNo'),
    altContactNo: initial?.altContactNo ?? bodyText(fixing, 'altContactNo'),
    email: initial?.email ?? bodyText(fixing, 'email'),
    address: initial?.address ?? bodyText(fixing, 'address'),
    city: initial?.city ?? bodyText(fixing, 'city'),
    notes: initial?.notes ?? bodyText(fixing, 'notes'),
  });

  function optionalFields() {
    return {
      ...(form.nameUr.trim() ? { nameUr: form.nameUr.trim() } : {}),
      ...(form.contactPerson.trim() ? { contactPerson: form.contactPerson.trim() } : {}),
      ...(form.contactNo.trim() ? { contactNo: form.contactNo.trim() } : {}),
      ...(form.altContactNo.trim() ? { altContactNo: form.altContactNo.trim() } : {}),
      ...(form.email.trim() ? { email: form.email.trim() } : {}),
      ...(form.address.trim() ? { address: form.address.trim() } : {}),
      ...(form.city.trim() ? { city: form.city.trim() } : {}),
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    };
  }

  const save = useMutation({
    mutationFn: (): Promise<ReferenceSaved> => {
      if (initial) {
        return api
          .patch(`/api/v1/suppliers/${initial.id}`, { nameEn: form.nameEn.trim(), ...optionalFields() })
          .then(() => ({ code: initial.code, waiting: false }));
      }
      // Through the outbox (.docs/023): made now with a line, kept on this PC without.
      const name = form.nameEn.trim();
      const body = { nameEn: name, ...optionalFields() };
      if (fixing) return resendReference(fixing, body, `New supplier: ${name}`, name);
      return saveReference([createOp('supplier.create', '/api/v1/suppliers', body, `New supplier: ${name}`)], name);
    },
    onSuccess: (result) =>
      onSaved(
        editing
          ? `${result.code} updated.`
          : result.waiting
            ? `“${form.nameEn.trim()}” is saved on this PC. It gets its code when it is sent.`
            : `${result.code} added to the supplier list.`,
      ),
  });

  return (
    <FormPanel title={editing ? `Edit ${initial.code}` : fixing ? 'Put right a refused supplier' : 'Add a supplier'} icon={<AppIcon kind="supplier" size={18} />}>
      {fixing?.lastError && <ErrorNote message={fixing.lastError} />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormGroup title="Basic details" columns={3} card>
          {editing && (
            <Field
              label="Code"
              mono
              value={initial.code}
              onChange={() => {}}
              disabled
              hint="Given when it was created, and never changes"
            />
          )}
          <Field label="Name" required value={form.nameEn} onChange={(nameEn) => setForm((f) => ({ ...f, nameEn }))} placeholder="Punjab Feed Mills" />
          <Field label="Name (Urdu)" urdu value={form.nameUr} onChange={(nameUr) => setForm((f) => ({ ...f, nameUr }))} />
        </FormGroup>

        <FormGroup title="Contact & location" columns={3} card>
          <Field label="Contact person" value={form.contactPerson} onChange={(contactPerson) => setForm((f) => ({ ...f, contactPerson }))} />
          <Field label="Contact number" value={form.contactNo} onChange={(contactNo) => setForm((f) => ({ ...f, contactNo }))} placeholder="0300-1234567" />
          <Field label="Alt. contact number" value={form.altContactNo} onChange={(altContactNo) => setForm((f) => ({ ...f, altContactNo }))} />
          <Field label="Email" type="email" value={form.email} onChange={(email) => setForm((f) => ({ ...f, email }))} />
          <Field label="Address" value={form.address} onChange={(address) => setForm((f) => ({ ...f, address }))} />
          <Field label="City" value={form.city} onChange={(city) => setForm((f) => ({ ...f, city }))} placeholder="Okara" />
        </FormGroup>

        <FormGroup title="Additional information" columns={2} card>
          <Field label="Notes" value={form.notes} onChange={(notes) => setForm((f) => ({ ...f, notes }))} span="full" />
        </FormGroup>

        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

        <FormActions onCancel={onCancel} cancelLabel={editing ? 'Cancel' : 'Back to list'}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add supplier'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}

function SuppliersPanel({ fixOperation }: { fixOperation?: Operation | undefined }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [form, setForm] = useState<'create' | 'edit' | 'fix' | null>(fixOperation ? 'fix' : null);
  const [fixOp, setFixOp] = useState<Operation | null>(fixOperation ?? null);
  const online = useConnection((s) => s.online);
  const [notice, setNotice] = useState<Notice>(null);

  const list = useQuery({
    queryKey: ['suppliers', 'reference', search, showRetired],
    queryFn: () => api.get('/api/v1/suppliers', { search: search || undefined, includeInactive: showRetired ? 'true' : undefined }),
  });
  const { data, isPending, isError, error } = list;

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['suppliers'] });

  const retire = useMutation({
    mutationFn: (id: string) => api.delete<RetireResult>(`/api/v1/suppliers/${id}`),
    onSuccess: (result) => { invalidate(); afterRetire(result); setNotice({ tone: 'ok', message: result.message }); },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not delete the supplier') }),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => api.patch(`/api/v1/suppliers/${id}`, { isActive: true }),
    onSuccess: () => { invalidate(); page.patch({ isActive: true }); setNotice({ tone: 'ok', message: 'Supplier reactivated.' }); },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not reactivate the supplier') }),
  });

  // Added on this PC and not made by the server yet (.docs/023): first in the list.
  const pending = usePendingInPanel(
    'supplier.create',
    (op) => { setFixOp(op); setForm('fix'); },
    (message) => setNotice({ tone: 'ok', message }),
  );
  const typed = search.trim().toLowerCase();
  const rows = [
    ...pending.ops.map(pendingSupplier).filter((r) => !typed || r.nameEn.toLowerCase().includes(typed)),
    ...(data ?? []),
  ];
  const page = useOpenRecord<SupplierRow>(rows);
  const selected = rows.find((r) => r.id === selectedKey) ?? (page.row?.id === selectedKey ? page.row : null);

  function show(row: SupplierRow | null) {
    if (row) {
      setSelectedKey(row.id);
      page.open(row);
    } else page.close();
    setNotice(null);
  }

  /** Deleted outright: nothing left to show. Switched off: still there, deleted. */
  function afterRetire(result: RetireResult) {
    if (result.outcome === 'deleted') page.close();
    else page.patch({ isActive: false });
  }

  function rowActions(row: SupplierRow): DesktopAction[] {
    const opener: DesktopAction = { label: 'Open', icon: 'details', shortcut: 'Enter', onSelect: () => show(row), allowed: true };
    const op = pending.opOf(row);
    if (op) return [opener, ...pending.actionsFor(op, can('inv.supplier.create'))];
    return [opener, ...needsTheLine(serverRowActions(row), online)];
  }

  function serverRowActions(row: SupplierRow): DesktopAction[] {
    return [
      { label: 'Edit', icon: 'edit', onSelect: () => { setSelectedKey(row.id); setForm('edit'); }, allowed: can('inv.supplier.update') },
      row.isActive
        ? {
            label: 'Delete', icon: 'retire',
            onSelect: () => {
              if (window.confirm(`Delete ${row.code}? If it was never used, it is deleted. If it was used, it is switched off and its history stays.`)) {
                setSelectedKey(row.id);
                retire.mutate(row.id);
              }
            },
            allowed: can('inv.supplier.delete'),
            danger: true,
            separatorBefore: true,
          }
        : { label: 'Reactivate', icon: 'reactivate', onSelect: () => { setSelectedKey(row.id); reactivate.mutate(row.id); }, allowed: can('inv.supplier.update'), separatorBefore: true },
    ];
  }

  const toolbarActions: DesktopAction[] = [
    { label: 'Add supplier', icon: 'add', primary: true, onSelect: () => setForm('create'), allowed: can('inv.supplier.create') },
  ];

  const columns: Array<GridColumn<SupplierRow>> = [
    { key: 'code', header: 'Code', width: 110, render: (r) => <span className="mono">{r.code}</span> },
    {
      key: 'name',
      header: 'Supplier',
      width: 240,
      render: (r) => (
        <>
          <AppIcon kind={'supplier'} />{' '}
          {r.nameEn}
          {r.nameUr && <span className="urdu"> · {r.nameUr}</span>}
        </>
      ),
    },
    { key: 'contact', header: 'Contact', width: 160, render: (r) => <span className="dim">{r.contactNo ?? '—'}</span> },
    { key: 'city', header: 'City', width: 120, render: (r) => <span className="dim">{r.city ?? '—'}</span> },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      render: (r) => {
        const op = pending.opOf(r);
        return op ? pending.statusOf(op) : (r.isActive ? <span className="pill">Active</span> : <span className="pill pill--alert">⚠ Deleted</span>);
      },
    },
  ];

  if (form) {
    return (
      <SupplierForm
        key={form === 'edit' && selected ? selected.id : form === 'fix' ? `fix-${fixOp?.id}` : 'create'}
        initial={form === 'edit' ? (selected ?? undefined) : undefined}
        fixing={form === 'fix' ? (fixOp ?? undefined) : undefined}
        onCancel={() => setForm(null)}
        onSaved={(message) => {
          invalidate();
          // Put right and sent again, it is new work: back to the list to find it.
          if (form === 'fix') page.close();
          setNotice({ tone: 'ok', message });
          setForm(null);
        }}
      />
    );
  }

  const openRow = page.row;
  if (openRow && isPendingRow(openRow)) {
    return (
      <PendingRecordPage
        rowId={openRow.id}
        backLabel="Back to suppliers"
        onBack={() => show(null)}
        onFix={(op) => { setFixOp(op); setForm('fix'); }}
        onOpen={(id) => show(rows.find((r) => r.id === id) ?? null)}
        onDone={(said) => { show(null); setNotice({ tone: 'ok', message: said }); }}
      />
    );
  }
  if (openRow) {
    return (
      <RecordPage
        backLabel="Back to suppliers"
        onBack={() => show(null)}
        icon={<AppIcon kind="supplier" size={18} />}
        code={openRow.code}
        name={openRow.nameEn}
        nameUr={openRow.nameUr}
        status={<MasterStatus row={{ isActive: openRow.isActive, needsReview: false }} />}
        actions={rowActions(openRow)}
        notice={notice}
      >
        <SupplierPage row={openRow} />
      </RecordPage>
    );
  }

  return (
    <div className="workbench__main">
      <div className="filterbar">
        <SearchBar value={search} onChange={setSearch} placeholder="Search suppliers by code, name, city or contact…" />
        <label className="settings__toggle">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
          <span className="field__label">Show deleted</span>
        </label>
      </div>

      <ActionBar actions={toolbarActions} />

      {isError && <ErrorNote message={error.message} />}
      {notice && (notice.tone === 'ok' ? <OkNote message={notice.message} /> : <ErrorNote message={notice.message} />)}

      <DataGrid
        id="reference-suppliers"
        label="Suppliers"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selectedKey}
        onSelect={setSelectedKey}
        // One tap opens it on its own screen.
        onOpen={show}
        openOnClick
        actions={rowActions}
        loading={isPending && !isOfflineGap(list)}
        empty={
          search
            ? 'No suppliers match your search.'
            : can('inv.supplier.create')
              ? 'No suppliers yet. Add one, or register one on the spot the next time stock is received.'
              : 'No suppliers yet. Ask an administrator to add one.'
        }
      />

    </div>
  );
}
